// Pure percentile-plot logic shared with <vf-apy-chart>'s position view
// (view-options.ts): the piecewise display scale, scatter series, hidden
// display axis, footnote sentences, and per-epoch tooltip rows. Data +
// theme in, strings/option out — no DOM construction.
//
// Honesty rules enforced here:
// - A null percentile is a gap: it never enters N, M, or the median, and
//   its count (K) is disclosed in the footnote whenever K > 0. Nothing is
//   interpolated, nothing coerced to 0.
// - The y scale is a documented derivation, not a distortion: a dynamic
//   floor (lowest observed decade, clamped to [0, 80]) plus one knee at
//   p90 mapping [F, 90] onto the lower 45% of the plot and [90, 100] onto
//   the upper 55%. Reference lines labeled with TRUE percentile values are
//   the axis (the raw 0-1 display axis is hidden), the visibly unequal
//   grid spacing shows the expansion, and the footnote states the mapping
//   (percentileScaleNote). Tooltips report true percentiles only.
// - Every aggregate names its denominator ("N of M epochs"; median over
//   the M served percentiles).
//
// Unit of the input series (verified against the API project's migration
// 0034): our_base_percentile_pct = SQL percent_rank() * 100 over the
// epoch's measured base (inflation-only) delegator-APY distribution,
// ordered ascending — 0-100, higher = larger share of measured validators
// strictly below our value; rows tied with ours share the same percentile.

import type { ScatterSeriesOption } from "echarts/charts";

import { MONO_FONT } from "./chart-options";
import { formatCount, formatPct } from "./format";
import type { ThemeTokens } from "./theme";
import type { TooltipLine } from "./tooltip";

/** The p90 knee/threshold of the display scale (percentile units). */
export const P90_THRESHOLD = 90;

export const CONSISTENCY_SERIES = "our percentile";

// --- piecewise-linear percentile display scale ---------------------------
//
// True percentile p maps onto a hidden [0, 1] display axis with one knee
// at p90: [floor, 90] -> [0, KNEE_DISPLAY] and [90, 100] ->
// [KNEE_DISPLAY, 1]. The floor F is the lowest observed decade of the
// plotted data, clamped to [0, 80], so 90 - F >= 10 always (no degenerate
// segment). Labeled reference lines carry the TRUE percentile values.

/** Display-axis position of the p90 knee: the 90-100 range spans 1 - this. */
export const KNEE_DISPLAY = 0.45;

/** Ceiling of the dynamic floor: keeps the lower segment at least [80, 90]. */
export const FLOOR_MAX = 80;

/**
 * Dynamic floor F: min over the served (non-null) percentiles, rounded
 * down to a multiple of 10, clamped into [0, FLOOR_MAX]. All-null or
 * empty input yields 0. Computed from the same array the dots plot.
 */
export function percentileFloor(percentiles: readonly (number | null)[]): number {
  let min = Number.POSITIVE_INFINITY;
  for (const p of percentiles) {
    if (p !== null && p !== undefined && p < min) {
      min = p;
    }
  }
  if (!Number.isFinite(min)) {
    return 0;
  }
  const decade = Math.floor(min / 10) * 10;
  return Math.min(FLOOR_MAX, Math.max(0, decade));
}

/**
 * Piecewise-linear transform: true percentile -> display position.
 *
 * Total on all inputs, no clamping: floor maps to 0, 90 to KNEE_DISPLAY,
 * 100 to 1, and a value below the floor maps linearly below 0 (it cannot
 * occur when the floor came from percentileFloor over the same data, but
 * if given one this maps it honestly rather than pinning it to 0).
 */
export function percentileToDisplay(p: number, floor: number): number {
  if (p <= P90_THRESHOLD) {
    return ((p - floor) / (P90_THRESHOLD - floor)) * KNEE_DISPLAY;
  }
  return KNEE_DISPLAY + ((p - P90_THRESHOLD) / (100 - P90_THRESHOLD)) * (1 - KNEE_DISPLAY);
}

/** True-percentile values of the labeled reference lines: F, every 10 through 90, then 95, 100. */
export function percentileGridLevels(floor: number): number[] {
  const levels: number[] = [];
  for (let v = floor; v <= P90_THRESHOLD; v += 10) {
    levels.push(v);
  }
  levels.push(95, 100);
  return levels;
}

/** Footnote sentence documenting the display scale; `floor` from percentileFloor. */
export function percentileScaleNote(floor: number): string {
  return (
    "y scale: piecewise linear — the 90-100 range spans about half the plot height, " +
    `and the axis starts at the lowest observed decade (p${floor}); ` +
    "grid lines are labeled with true percentiles."
  );
}

/**
 * Percentile definition sentence, shared verbatim by this card's footnote
 * and the full chart's position-view footnote (one derivation statement,
 * two hosts).
 */
export const PERCENTILE_DEFINITION_NOTE =
  "percentile = percent_rank of measured base delegator APY among n validators in that epoch; " +
  "ties share a percentile; epochs without a served value are blank.";

/** Structural share of the p90-100 region — the anchor for any occupancy read. */
export const P90_REGION_NOTE =
  "The p90-100 region holds 10% of measured validators each epoch by construction; " +
  "occupancy above 10% of epochs measures persistence.";

/** Median of a non-empty list (mean of the two middle values on even counts); null when empty. */
export function medianOf(values: readonly number[]): number | null {
  if (values.length === 0) {
    return null;
  }
  const v = [...values].sort((a, b) => a - b);
  const mid = Math.floor(v.length / 2);
  if (v.length % 2 === 1) {
    return v[mid] ?? null;
  }
  const lo = v[mid - 1];
  const hi = v[mid];
  return lo !== undefined && hi !== undefined ? (lo + hi) / 2 : null;
}

/** English ordinal suffix: 1st, 2nd, 3rd, 4th, 11th-13th, 21st, ... */
export function ordinal(n: number): string {
  const rem100 = Math.abs(n) % 100;
  if (rem100 >= 11 && rem100 <= 13) {
    return `${n}th`;
  }
  switch (Math.abs(n) % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

// No YEAR_SECS constant here: trailingRealizedApy does NOT annualize — it
// trusts the daemon's already-annualized per-epoch APY, and the YEAR/durᵢ
// factor cancels in the duration weighting. Annualization is daemon-side only.

/** Seconds per day. */
const DAY_SECS = 86_400;

/** Result of {@link trailingRealizedApy}. */
export interface TrailingRealizedApy {
  /** Duration-weighted realized APY (%) over the window; null when no epoch is served. */
  apy: number | null;
  /** Epochs with BOTH a non-null value and a non-null duration inside the window. */
  served: number;
  /** Real elapsed days across the served epochs — the annualization base and the disclosed window span. */
  spanDays: number;
}

/**
 * Trailing realized APY: the yield actually realized over the last
 * `targetDays` of real elapsed time, annualized.
 *
 * Walks epochs newest-first, accumulating each epoch's real duration until the
 * span reaches `targetDays`, then returns the duration-weighted geometric mean
 * of the per-epoch annualized APYs:
 *
 *   ( exp( Σ dᵢ·ln(1 + aᵢ/100) / Σ dᵢ ) − 1 ) × 100
 *
 * Each epoch's weight `dᵢ` is its own duration, so the `YEAR/durᵢ` factor
 * inside `aᵢ` cancels exactly: the result is `M^(YEAR/T) − 1` where
 * `M = ∏(1+rateᵢ)` is the realized multiplier and `T = Σ durᵢ` the true
 * elapsed time. A long epoch contributes proportionally more seconds AND more
 * reward, so the per-epoch annualization artifact (a single long/short epoch
 * over- or under-stating its annualized rate) cancels at any window length —
 * unlike a single epoch's APY, which carries it. Epochs with a null value or
 * null/zero duration never enter the number (never approximated); `served`
 * discloses how many did. `apy` is null only when the window has no served
 * epoch.
 */
export function trailingRealizedApy(
  apyPct: readonly (number | null)[],
  durationSecs: readonly (number | null)[],
  targetDays: number,
): TrailingRealizedApy {
  const targetSecs = targetDays * DAY_SECS;
  let logSum = 0;
  let servedSecs = 0;
  let served = 0;
  let spanSecs = 0;
  for (let i = apyPct.length - 1; i >= 0; i -= 1) {
    const d = durationSecs[i];
    if (d === null || d === undefined || d <= 0) {
      // No duration: the epoch cannot be weighted or counted toward the span.
      continue;
    }
    spanSecs += d;
    const a = apyPct[i];
    if (a !== null && a !== undefined) {
      const term = Math.log(1 + a / 100);
      // Guard corrupt rows: a <= -100 yields NaN / -Infinity, which would
      // poison the whole window (and NaN passes a `!== null` gate downstream,
      // rendering "NaN%"). Skip such an epoch, treating it as unserved.
      if (Number.isFinite(term)) {
        logSum += d * term;
        servedSecs += d;
        served += 1;
      }
    }
    if (spanSecs >= targetSecs) {
      break;
    }
  }
  const raw = servedSecs > 0 ? (Math.exp(logSum / servedSecs) - 1) * 100 : null;
  const apy = raw !== null && Number.isFinite(raw) ? raw : null;
  return { apy, served, spanDays: servedSecs / DAY_SECS };
}

/** Result of {@link trailingMedianByDays}. */
export interface TrailingMedianByDays {
  /** Median of served values inside the window; null when none served. */
  median: number | null;
  /** Values (non-null) that entered the median. */
  served: number;
  /** Epochs (served or not, with a duration) spanned by the window. */
  windowEpochs: number;
}

/**
 * Median of a per-epoch series over the last `targetDays` of real elapsed
 * time — the day-anchored counterpart to the epoch-count trailing median.
 *
 * Same window rule as {@link trailingRealizedApy}: walk newest-first,
 * accumulating each epoch's real duration until the span reaches `targetDays`.
 * Nulls never enter the median (disclosed via `served`, never bridged);
 * epochs without a duration cannot be placed in the window and are skipped.
 * Lets the position cell stand on the SAME trailing-days window as the
 * headline APY instead of a separate epoch count.
 */
export function trailingMedianByDays(
  values: readonly (number | null)[],
  durationSecs: readonly (number | null)[],
  targetDays: number,
): TrailingMedianByDays {
  const targetSecs = targetDays * DAY_SECS;
  const served: number[] = [];
  let spanSecs = 0;
  let windowEpochs = 0;
  for (let i = values.length - 1; i >= 0; i -= 1) {
    const d = durationSecs[i];
    if (d === null || d === undefined || d <= 0) {
      continue;
    }
    spanSecs += d;
    windowEpochs += 1;
    const v = values[i];
    if (v !== null && v !== undefined) {
      served.push(v);
    }
    if (spanSecs >= targetSecs) {
      break;
    }
  }
  return { median: medianOf(served), served: served.length, windowEpochs };
}

/** One epoch's tooltip facts, read from the raw byEpoch join (never fabricated). */
export interface EpochPercentileFacts {
  percentile: number | null;
  /** 1-based competition rank inside the epoch's measured base distribution. */
  rank: number | null;
  /** Measured validators in that epoch's base distribution (the rank's denominator). */
  distributionValidators: number | null;
  ourBaseApyPct: number | null;
  clusterP50BaseApyPct: number | null;
}

/**
 * Tooltip rows for one epoch. Absent facts render as explicit written
 * absence ("not served"), except the rank row, which is omitted entirely
 * unless BOTH the rank and its denominator are served — a rank without its
 * denominator is not a statable fact.
 */
export function percentileTooltipLines(f: EpochPercentileFacts, t: ThemeTokens): TooltipLine[] {
  const rows: TooltipLine[] = [
    {
      label: "percentile",
      value: f.percentile === null ? "not served this epoch" : f.percentile.toFixed(1),
      color: t.ourLine,
    },
  ];
  if (f.rank !== null && f.distributionValidators !== null) {
    rows.push({
      label: "rank",
      value: `${formatCount(f.rank)} of ${formatCount(f.distributionValidators)} validators`,
    });
  }
  rows.push({
    label: "our base APY",
    value: f.ourBaseApyPct === null ? "not served" : formatPct(f.ourBaseApyPct),
  });
  rows.push({
    label: "cluster p50 base APY",
    value: f.clusterP50BaseApyPct === null ? "not served" : formatPct(f.clusterP50BaseApyPct),
    color: t.medianLine,
  });
  return rows;
}

/** One plotted dot: display-axis value plus the true percentile it encodes. */
export interface PercentilePoint {
  value: number;
  truePercentile: number;
}

/**
 * Percentile scatter series on the piecewise-linear display scale.
 *
 * Per-epoch dots (a null epoch is simply absent — no connecting line, no
 * bridging) plotted at percentileToDisplay positions, each point carrying
 * its true percentile in the payload. The scale itself renders as labeled
 * hairline reference lines at percentileGridLevels (splitLine ink, muted
 * mono labels with true values), with two distinguished lines: 90 keeps
 * the p90 hairline's bandEdge ink, and 95 — the level the dots usually
 * live above — is dashed in axisLine ink, a step more present than the
 * plain gridlines. The 90-100 reference fill (theme.percentileBand /
 * theme.percentileBandFillOpacity — the percentile-plot tint, not the
 * main chart's band fill) maps through the same transform, spanning
 * display KNEE_DISPLAY..1.
 *
 * Shared by this card's strip and the full chart's position view
 * (view-options.ts) — one encoding source, two hosts.
 */
export function percentileScatterSeries(
  percentiles: readonly (number | null)[],
  t: ThemeTokens,
): ScatterSeriesOption {
  const floor = percentileFloor(percentiles);
  const data: (PercentilePoint | null)[] = percentiles.map((p) =>
    p === null || p === undefined ? null : { value: percentileToDisplay(p, floor), truePercentile: p },
  );
  return {
    name: CONSISTENCY_SERIES,
    type: "scatter",
    // Payload objects carry truePercentile alongside the display value;
    // the scatter data item type has no slot for extra fields, hence the cast.
    data: data as ScatterSeriesOption["data"],
    symbol: "circle",
    symbolSize: 4,
    itemStyle: { color: t.ourLine },
    emphasis: { focus: "none" },
    markArea: {
      silent: true,
      itemStyle: { color: t.percentileBand, opacity: t.percentileBandFillOpacity },
      label: { show: false },
      data: [
        [
          { yAxis: percentileToDisplay(P90_THRESHOLD, floor) },
          { yAxis: percentileToDisplay(100, floor) },
        ],
      ],
    },
    markLine: {
      silent: true,
      symbol: "none",
      label: {
        show: true,
        position: "insideEndTop",
        fontSize: 10,
        fontFamily: MONO_FONT,
        color: t.textMuted,
      },
      lineStyle: { color: t.splitLine, width: 1, type: "solid" },
      data: percentileGridLevels(floor).map((level) => ({
        yAxis: percentileToDisplay(level, floor),
        label: { formatter: String(level) },
        // 90: the p90 hairline mapped through the transform. 95: the
        // habitual reference — dashed, one step more present than the
        // plain gridlines, still hairline weight.
        ...(level === P90_THRESHOLD ? { lineStyle: { color: t.bandEdge } } : {}),
        ...(level === 95 ? { lineStyle: { color: t.axisLine, type: "dashed" as const } } : {}),
      })),
    },
    z: 3,
  };
}

/**
 * Hidden linear display axis for the piecewise scale: fixed [0, 1], no
 * ticks, no labels, no split lines — the labeled reference lines drawn by
 * percentileScatterSeries ARE the axis. Only the frame line stays.
 */
export function percentileYAxis(t: ThemeTokens) {
  return {
    type: "value" as const,
    min: 0,
    max: 1,
    axisLabel: { show: false },
    axisTick: { show: false },
    axisLine: { show: true, lineStyle: { color: t.axisLine } },
    splitLine: { show: false },
  };
}
